/**
 * Manages the global loading state and provides a context for loading state management
 */
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'

import { LoadingStateContext, type LoadingStateContextValue } from './LoadingContext'

interface GlobalLoadingProviderProps {
  children: ReactNode
  /** Minimum time in milliseconds to display the loader (default: 2500) */
  minimumDisplayTime?: number
  /** Custom loading component to display */
  loadingComponent?: ReactNode
  /** Function to determine if loader should be shown based on path (optional) */
  shouldShowLoaderForPath?: () => boolean
}

export function GlobalLoadingProvider({
  children,
  minimumDisplayTime = 2500,
  loadingComponent,
  shouldShowLoaderForPath,
}: GlobalLoadingProviderProps) {
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({})
  const [hasCompletedInitialLoad, setHasCompletedInitialLoad] = useState(false)

  // Refs for minimum display time logic (using useSyncExternalStore pattern)
  const loadingStartTimeRef = useRef<number | null>(null)
  const isWaitingForMinTimeRef = useRef(false)
  const subscribersRef = useRef(new Set<() => void>())

  // Check if any providers loading states are active
  const isAnyLoading = useMemo(() => {
    return Object.values(loadingStates).some((isLoading) => isLoading)
  }, [loadingStates])

  const isProviderLoading = Object.keys(loadingStates).length > 0

  // Mark initial load as complete once providers finish loading
  useEffect(() => {
    if (isProviderLoading && !isAnyLoading && !hasCompletedInitialLoad) {
      // Use setTimeout to defer state update out of effect body
      const timeout = setTimeout(() => {
        setHasCompletedInitialLoad(true)
      }, 0)
      return () => clearTimeout(timeout)
    }
  }, [isProviderLoading, isAnyLoading, hasCompletedInitialLoad])

  const shouldShowLoader = !hasCompletedInitialLoad || isAnyLoading

  // Subscribe function for useSyncExternalStore
  const subscribe = useCallback((callback: () => void) => {
    subscribersRef.current.add(callback)
    return () => {
      subscribersRef.current.delete(callback)
    }
  }, [])

  // Notify all subscribers when state changes
  const notifySubscribers = useCallback(() => {
    subscribersRef.current.forEach((callback) => callback())
  }, [])

  // Get the current snapshot of whether we're waiting for min time
  const getIsWaitingSnapshot = useCallback(() => {
    return isWaitingForMinTimeRef.current
  }, [])

  // Implement minimum display time logic
  useEffect(() => {
    if (shouldShowLoader) {
      // Start loading - record the start time
      if (loadingStartTimeRef.current === null) {
        loadingStartTimeRef.current = Date.now()
        isWaitingForMinTimeRef.current = false
      }
      return
    }

    // Loading finished - check if we need minimum display time
    if (loadingStartTimeRef.current === null) {
      return
    }

    const elapsed = Date.now() - loadingStartTimeRef.current

    if (elapsed >= minimumDisplayTime) {
      // Already displayed for at least minimum time, reset immediately
      loadingStartTimeRef.current = null
      isWaitingForMinTimeRef.current = false
      return
    }

    // Wait until minimum display time is reached
    const remainingTime = minimumDisplayTime - elapsed
    isWaitingForMinTimeRef.current = true

    const timeout = setTimeout(() => {
      isWaitingForMinTimeRef.current = false
      loadingStartTimeRef.current = null
      notifySubscribers()
    }, remainingTime)

    return () => clearTimeout(timeout)
  }, [shouldShowLoader, minimumDisplayTime, notifySubscribers])

  // Use useSyncExternalStore to subscribe to waiting state
  const isWaitingForMinTime = useSyncExternalStore(subscribe, getIsWaitingSnapshot, getIsWaitingSnapshot)

  // Show loader if currently loading OR if waiting for minimum display time
  const showLoaderWithMinTime = shouldShowLoader || isWaitingForMinTime

  // Allow consumer to override loader visibility based on path
  const showLoader = shouldShowLoaderForPath ? shouldShowLoaderForPath() && showLoaderWithMinTime : showLoaderWithMinTime

  // Block Router when showing loader
  const isGlobalLoaderActive = showLoader

  const registerLoadingState = useCallback((name: string, isLoading: boolean) => {
    setLoadingStates((prev) => ({
      ...prev,
      [name]: isLoading,
    }))
  }, [])

  const unregisterLoadingState = useCallback((name: string) => {
    setLoadingStates((prev) => {
      const next = { ...prev }
      delete next[name]
      return next
    })
  }, [])

  const contextValue: LoadingStateContextValue = useMemo(
    () => ({
      registerLoadingState,
      unregisterLoadingState,
      isLoading: isAnyLoading,
      isGlobalLoaderActive,
    }),
    [registerLoadingState, unregisterLoadingState, isAnyLoading, isGlobalLoaderActive],
  )

  return (
    <LoadingStateContext.Provider value={contextValue}>
      {showLoader && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white dark:bg-gray-900 transition-colors duration-200">
          {loadingComponent}
        </div>
      )}
      {/*
        Always render children so providers can mount and register their loading states.
        The DeferredRouter component prevents Router and all pages from rendering until
        the global loader completes, ensuring pages don't mount and make HTTP requests
        during the animation or start painting the page.
        This gives the animation full CPU/GPU resources for a very smooth UX.
      */}
      {children}
    </LoadingStateContext.Provider>
  )
}
